/**
 * LabDecksAdmin.tsx — Admin > Integrations > Lab Decks.
 *
 * Legacy's Lab Decks (lib/admin/admin-labdeck-list.php): slide decks the Quest
 * lab results viewer walks a patient through. Decks (A–Z) and their slides on
 * one board (CategoryBoard). The slides keep their order — it is the order the
 * viewer shows them in — so a slide drags to reorder it inside its deck, or
 * onto another deck (the board's ordered mode; every other admin board only
 * re-files). A slide's tests are edited inside the slide
 * (LabDeckForms). Delete is soft for decks and slides (the viewer skips them);
 * legacy deleted them outright and left their slides and tests behind.
 *
 * On a Locked page (useAdminPageLevel) the board neither drags nor shows its
 * icons (CategoryBoard, AdminRowAction), the subtitle and empty board invite
 * no drag or add, and a live slide still opens, read-only, with its tests.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { AdminBoardPage, AdminListEmpty, AdminListToolbar } from '@/components/admin/AdminList';
import { AdminRowAction } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { CategoryBoard, boardDragHint, type BoardCategory } from '@/components/admin/CategoryBoard';
import { compareTitles } from '@/components/admin/categoryBoardState';
import { LabDeckModal, LabSlideModal } from '@/components/admin/LabDeckForms';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  deleteLabDeck,
  deleteLabSlide,
  listBiomarkers,
  listLabDecks,
  reorderLabSlides,
  restoreLabDeck,
  restoreLabSlide,
  type Biomarker,
  type LabDeck,
  type LabSlide,
} from '@/services/labDecksAdmin.service';
import { toast } from '@/store/toastStore';

const byOrder = <T extends { order: number; id: number }>(a: T, b: T) => a.order - b.order || a.id - b.id;
const deckName = (d: { title: string; id: number }) => d.title.trim() || `Deck #${d.id}`;
const byTitle = (a: { title: string; id: number }, b: { title: string; id: number }) => compareTitles(deckName(a), deckName(b)) || a.id - b.id;
const slideName = (s: { title: string; id: number }) => s.title.trim() || `Slide #${s.id}`;

const SLIDE_COLUMNS = (
  <div className="flex items-center gap-3 pr-2">
    <span className="min-w-0 flex-1">Slide</span>
    <span className="w-32 shrink-0">Type</span>
    <span className="w-20 shrink-0 text-right">Tests</span>
  </div>
);

export function LabDecksAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [decks, setDecks] = useState<Omit<LabDeck, 'slides'>[]>([]);
  const [slides, setSlides] = useState<LabSlide[]>([]);
  const [biomarkers, setBiomarkers] = useState<Biomarker[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [deckModal, setDeckModal] = useState<{ deck: LabDeck | null } | null>(null);
  const [slideModal, setSlideModal] = useState<{ slideId: number | null; deckId: number } | null>(null);
  const [deleting, setDeleting] = useState<{ kind: 'deck' | 'slide'; id: number; name: string } | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const rows = await listLabDecks();
      setDecks(rows.map(({ slides: _s, ...d }) => d));
      setSlides(rows.flatMap((d) => d.slides));
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load lab decks'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    listBiomarkers()
      .then(setBiomarkers)
      .catch(() => setBiomarkers([]));
  }, [load]);

  const query = search.trim().toLowerCase();
  const narrowed = query !== '';

  const board = useMemo(() => {
    const groups: BoardCategory[] = [];
    const records: LabSlide[] = [];
    for (const d of [...decks].sort(byTitle)) {
      if (!showDeleted && !d.active) continue;
      const deckHit = query === '' || deckName(d).toLowerCase().includes(query);
      const rows = slides
        .filter((s) => s.deckId === d.id && (showDeleted || s.active) && (deckHit || slideName(s).toLowerCase().includes(query)))
        .sort(byOrder);
      if (narrowed && !deckHit && rows.length === 0) continue;
      groups.push({ id: d.id, title: deckName(d), deleted: !d.active });
      records.push(...rows);
    }
    return { groups, records };
  }, [decks, slides, showDeleted, query, narrowed]);

  const liveDecks = useMemo(() => [...decks].sort(byTitle).filter((d) => d.active).map((d) => ({ ...d, slides: [] })), [decks]);
  const editingSlide = slideModal?.slideId ? slides.find((s) => s.id === slideModal.slideId) ?? null : null;
  const slideDecks = useMemo(() => {
    const own = editingSlide ? decks.find((d) => d.id === editingSlide.deckId) : undefined;
    return own && !own.active ? [...liveDecks, { ...own, slides: [] }] : liveDecks;
  }, [editingSlide, decks, liveDecks]);

  async function reorderSlides(deckId: number, ids: number[]) {
    await reorderLabSlides(deckId, ids);
    setSlides((prev) => prev.map((s) => (ids.includes(s.id) ? { ...s, deckId, order: ids.indexOf(s.id) + 1 } : s)));
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      if (deleting.kind === 'deck') await deleteLabDeck(deleting.id);
      else await deleteLabSlide(deleting.id);
      toast.success(deleting.kind === 'deck' ? 'Deck deleted' : 'Slide deleted');
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, `The ${deleting.kind} could not be deleted.`));
    }
  }

  async function handleRestore(kind: 'deck' | 'slide', id: number) {
    try {
      if (kind === 'deck') await restoreLabDeck(id);
      else await restoreLabSlide(id);
      toast.success(kind === 'deck' ? 'Deck restored' : 'Slide restored');
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, `The ${kind} could not be restored.`));
    }
  }

  function slideRow(s: LabSlide) {
    return (
      <button
        type="button"
        className={cn('flex w-full items-center gap-3 pr-2 text-left', !s.active && 'text-slate-400 line-through')}
        onClick={() => s.active && setSlideModal({ slideId: s.id, deckId: s.deckId })}
        disabled={!s.active}
        data-testid="slide-row"
      >
        <span className="min-w-0 flex-1 truncate">{slideName(s)}</span>
        <span className="w-32 shrink-0 text-slate-500">{s.kind === 'html' ? 'HTML Content' : 'Lab Results'}</span>
        <span className="w-20 shrink-0 text-right text-slate-500">{s.kind === 'lab' ? s.testCount : '—'}</span>
      </button>
    );
  }

  return (
    <AdminBoardPage
      title="Lab Decks"
      subtitle={
        locked
          ? 'Slide decks the Quest lab results viewer walks a patient through.'
          : 'Slide decks the Quest lab results viewer walks a patient through. Drag a slide to reorder it, or onto another deck to move it there.'
      }
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setDeckModal({ deck: null })}>
          Add deck
        </Button>
      }
      toolbar={
        <AdminListToolbar
          className="mb-0"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search decks and slides…"
          searchLabel="Search lab decks"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="lab-decks-show-deleted"
        />
      }
      hint={boardDragHint({ searching: narrowed, filtering: false, showDeleted })}
      dialogs={
        <>
          {deckModal && (
            <LabDeckModal
              key={deckModal.deck?.id ?? 'new'}
              deck={deckModal.deck}
              onClose={() => setDeckModal(null)}
              onSaved={() => {
                setDeckModal(null);
                void load(true);
              }}
            />
          )}
          {slideModal && (
            <LabSlideModal
              key={slideModal.slideId ?? `new-${slideModal.deckId}`}
              slide={editingSlide}
              defaultDeckId={slideModal.deckId}
              decks={slideDecks}
              biomarkers={biomarkers}
              onTestsChanged={() => void load(true)}
              onClose={() => setSlideModal(null)}
              onSaved={(createdId) => {
                // A new slide stays open (as the edit form) so its tests can be added.
                void load(true).then(() => setSlideModal(createdId ? { slideId: createdId, deckId: slideModal.deckId } : null));
              }}
            />
          )}
          <ConfirmDialog
            isOpen={deleting !== null}
            onClose={() => setDeleting(null)}
            onConfirm={handleDelete}
            title={deleting ? `Delete ${deleting.kind} "${deleting.name}"?` : ''}
            message={
              deleting?.kind === 'deck'
                ? 'Patients stop seeing it, and its slides are hidden with it. You can restore it from Show deleted.'
                : 'Patients stop seeing it. You can restore it from Show deleted.'
            }
            confirmLabel={deleting ? `Delete ${deleting.kind}` : 'Delete'}
            variant="danger"
          />
        </>
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading lab decks…" />
      ) : board.groups.length === 0 ? (
        narrowed ? (
          <AdminListEmpty title="Nothing found" hint="Try adjusting your search." />
        ) : (
          <AdminListEmpty title="No decks yet" hint={locked ? undefined : 'Add a deck, then add its slides.'} />
        )
      ) : (
        <CategoryBoard<LabSlide>
          categories={board.groups}
          records={board.records}
          getRecordId={(s) => s.id}
          getRecordCategoryId={(s) => s.deckId}
          renderRecord={slideRow}
          columnHeader={SLIDE_COLUMNS}
          renderCategoryActions={(g) => {
            const d = decks.find((x) => x.id === g.id);
            if (!d) return null;
            const name = deckName(d);
            if (!d.active) return <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore('deck', d.id)} />;
            return (
              <>
                <AdminRowAction kind="add" title="Add slide" label={`Add slide to ${name}`} onClick={() => setSlideModal({ slideId: null, deckId: d.id })} />
                <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setDeckModal({ deck: { ...d, slides: [] } })} />
                <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting({ kind: 'deck', id: d.id, name })} />
              </>
            );
          }}
          renderRecordActions={(s) => {
            const name = slideName(s);
            if (!s.active) return <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore('slide', s.id)} />;
            return (
              <>
                <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setSlideModal({ slideId: s.id, deckId: s.deckId })} />
                <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting({ kind: 'slide', id: s.id, name })} />
              </>
            );
          }}
          isRecordDeleted={(s) => !s.active}
          ordered
          onReorderRecords={reorderSlides}
          onSaveError={(err) => toast.error(getErrorMessage(err, 'The new order could not be saved.'))}
          recordNoun={['slide', 'slides']}
          dragDisabled={narrowed || showDeleted}
        />
      )}
    </AdminBoardPage>
  );
}

export default LabDecksAdmin;
